import { Alert, Box, Button, Grid, Icon, IconButton, MenuItem, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import { ModalContext, ToastContent } from "../../../shared/components";
import { IVFormErros, useVForm, VForm, VTextField } from "../../../shared/forms";
import { useToast } from "../../../shared/hooks";
import { analysisService } from "../../../shared/services/api/analysis/analysisService";
import { PlanListEditor } from "./PlanListEditor";
import CloseIcon from "@mui/icons-material/Close";

interface RevertModalProps {
	isOpen: boolean;
	onClose: (options?: { shouldRefresh?: boolean }) => void;
	row: any;
}

const validationSchema = yup.object().shape({
	obs: yup.string().required("Campo obrigatório"),
	status: yup.string().required("Campo obrigatório"),
});

export const RevertModal: React.FC<RevertModalProps> = ({ isOpen, onClose, row }) => {
	const { formRef } = useVForm();
	const toastHandler = useToast();
	const [planlistValue, setPlanlistValue] = useState<string>("[]");

	const handleRequestClose = () => onClose({ shouldRefresh: false });

	const tryParsePlanlist = (val: unknown): any[] | null => {
		try {
			const parsed = typeof val === "string" ? JSON.parse(val) : val;
			return Array.isArray(parsed) ? parsed : null;
		} catch {
			return null;
		}
	};

	const getPlanIdFromEntry = (entry: any): string | null => {
		const candidate = entry?.planId ?? entry?.plan_id ?? entry?.plan?.id ?? entry?.plan?.planId ?? entry?.plan?.plan_id ?? entry?.id;
		if (candidate === null || candidate === undefined) return null;
		return String(candidate);
	};

	const hasRevisionPlanInList = useMemo(() => {
		const parsed = tryParsePlanlist(planlistValue) ?? [];
		return parsed.some((p: any) => {
			const id = getPlanIdFromEntry(p);
			if (!id) return false;
			const normalized = id.trim().toLowerCase();
			return normalized === "revision" || normalized === "revison" || normalized === "revisao";
		});
	}, [planlistValue]);

	const handleIncludeRevisionPlan = () => {
		const parsed = tryParsePlanlist(planlistValue) ?? [];
		const alreadyHas = parsed.some((p: any) => {
			const id = getPlanIdFromEntry(p);
			if (!id) return false;
			const normalized = id.trim().toLowerCase();
			return normalized === "revision" || normalized === "revison" || normalized === "revisao";
		});
		if (alreadyHas) return;

		const basePlan = parsed.find((p: any) => {
			const id = getPlanIdFromEntry(p);
			if (!id) return false;
			const normalized = id.trim().toLowerCase();
			return normalized !== "revision" && normalized !== "revison" && normalized !== "revisao";
		});

		const rentAmountCandidate =
			basePlan?.rent_amount ?? basePlan?.rentAmount ?? row?.rentAmount ?? row?.rent_amount ?? row?.client?.rentAmount ?? row?.client?.rent_amount ?? 0;
		const rent_amount = Number(rentAmountCandidate) || 0;
		const residentialFeeVal = 15;
		const commercialFeeVal = 15;
		const residentialFee_assurance_month_amount = rent_amount > 0 ? Number((rent_amount * (residentialFeeVal / 100)).toFixed(2)) : 0;
		const commercialFee_assurance_month_amount = rent_amount > 0 ? Number((rent_amount * (commercialFeeVal / 100)).toFixed(2)) : 0;

		const nowIso = new Date().toISOString();
		const generatedId = (globalThis as any)?.crypto?.randomUUID?.() ?? `revision-${Date.now()}`;
		const productId = basePlan?.productId ?? basePlan?.product_id ?? row?.productId ?? row?.product_id ?? "SEGURO";
		const rent_months_count = basePlan?.rent_months_count ?? 36;

		const revisionPlan = {
			id: generatedId,
			status: "inactive",
			clientId: row?.client?.id ?? row?.clientId ?? row?.client_id ?? "",
			description: "REVISION (Aluguel)",
			planId: "revision",
			productId,
			lmi: 32,
			residentialFee: "15.00",
			commercialFee: "15.00",
			bonus_amount: "0.00",
			flag_analysis: basePlan?.flag_analysis ?? "1",
			flag_renovation: basePlan?.flag_renovation ?? "0",
			flag_rateRent: basePlan?.flag_rateRent ?? "1",
			flag_rateCharge: basePlan?.flag_rateCharge ?? "0",
			plan: {
				id: "revision",
				description: "REVISION",
				lmi: 32,
				residential_fee: "15.00",
				commercial_fee: "15.00",
				bonus_amount: "0.00",
				coverage: 5,
				setup_transfer: "90.00",
				proposal_min: 1,
				status: "active",
				created_at: nowIso,
				updated_at: nowIso,
				product_id: productId,
				personalized: "false",
				coverage_acessories: "500.00",
			},
			coverage: 5,
			setup_transfer: "90.00",
			proposal_min: 1,
			comission_type: "empty",
			comission_value: "0.00",
			comission_proposal_min: 0,
			rent_months_count,
			rent_amount,
			commercialFee_assurance_month_amount,
			residentialFee_assurance_month_amount,
		};

		setPlanlistValue(JSON.stringify([...parsed, revisionPlan], null, 2));
	};

	const planRevisionStatus = useMemo(
		() => row?.client?.planRevision ?? row?.client?.plan_revision ?? row?.planRevision ?? row?.plan_revision ?? row?.planRevisionStatus ?? null,
		[row]
	);
	const revisionPlanId = useMemo(
		() =>
			(row?.client?.planRevision as any)?.planId ??
			(row?.client?.planRevision as any)?.plan_id ??
			(row?.client?.planRevision as any)?.id ??
			row?.planId ??
			row?.plan_id ??
			row?.client?.planId ??
			row?.client?.plan_id ??
			row?.revisionPlanId ??
			row?.revision_plan_id ??
			null,
		[row]
	);

	useEffect(() => {
		if (!isOpen) return;
		const raw = row?.planlist;
		if (!raw) {
			setPlanlistValue("[]");
			return;
		}
		try {
			const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
			setPlanlistValue(JSON.stringify(parsed, null, 2));
		} catch {
			setPlanlistValue(typeof raw === "string" ? raw : "[]");
		}
	}, [isOpen, row]);

	const normalizePlanlistForRevision = (planlist: unknown): string | undefined => {
		const planRevisionKey = row?.client?.planRevision ?? row?.client?.plan_revision ?? row?.planRevision ?? row?.plan_revision;
		if (planRevisionKey === null || planRevisionKey === undefined) {
			if (typeof planlist === "string") return planlist;
			if (Array.isArray(planlist)) return JSON.stringify(planlist);
			return undefined;
		}

		const isRevisionActive = (val: unknown): boolean => {
			if (val === null || val === undefined) return false;
			if (typeof val === "boolean") return val;
			if (typeof val === "number") return val > 0;
			if (typeof val === "string") {
				const normalized = val.trim().toLowerCase();
				return ["active", "ativo", "true", "1", "yes", "sim"].includes(normalized);
			}
			if (typeof val === "object") {
				const anyVal = val as any;
				return isRevisionActive(anyVal?.status ?? anyVal?.value ?? anyVal?.active);
			}
			return Boolean(val);
		};

		// Se a flag existe mas está inativa, não devemos alterar o planlist.
		if (!isRevisionActive(planRevisionKey)) {
			if (typeof planlist === "string") return planlist;
			if (Array.isArray(planlist)) return JSON.stringify(planlist);
			return undefined;
		}

		let parsed: any = planlist;
		try {
			parsed = typeof planlist === "string" ? JSON.parse(planlist) : planlist;
		} catch {
			return typeof planlist === "string" ? planlist : undefined;
		}
		if (!Array.isArray(parsed) || parsed.length === 0) {
			return typeof planlist === "string" ? planlist : Array.isArray(planlist) ? JSON.stringify(planlist) : undefined;
		}

		const toStatus = (val: unknown): string => String(val ?? "").trim().toLowerCase();
		const planIds = parsed.map(getPlanIdFromEntry).filter(Boolean) as string[];
		const planRevisionIdFromKey = getPlanIdFromEntry(planRevisionKey);
		const rowPlanId = row?.planId ?? row?.plan_id ?? row?.client?.planId ?? row?.client?.plan_id;
		const rowPlanIdStr = rowPlanId !== null && rowPlanId !== undefined ? String(rowPlanId) : null;
		const activePlan = parsed.find((p: any) => {
			const status = toStatus(p?.status ?? p?.plan?.status);
			return status === "active" || status === "ativo";
		});
		const activePlanId = getPlanIdFromEntry(activePlan);
		const explicitRevisionId = planIds.includes("revision") ? "revision" : null;

		const candidates = [planRevisionIdFromKey, rowPlanIdStr, activePlanId, explicitRevisionId, planIds[0]].filter(Boolean) as string[];
		const effectiveRevisionId = candidates.find((id) => planIds.includes(id));
		if (!effectiveRevisionId) return typeof planlist === "string" ? planlist : JSON.stringify(parsed);

		const normalized = parsed.map((p: any) => ({
			...p,
			status: getPlanIdFromEntry(p) === effectiveRevisionId ? "active" : "inactive",
		}));

		return JSON.stringify(normalized);
	};

	const handleSubmit = (dados: any) => {
		validationSchema
			.validate(dados, { abortEarly: false })
			.then((dadosValidados) => {
				const tryParsePlanlist = (val: unknown): any[] | null => {
					try {
						const parsed = typeof val === "string" ? JSON.parse(val) : val;
						return Array.isArray(parsed) ? parsed : null;
					} catch {
						return null;
					}
				};

				const originalParsed = tryParsePlanlist(row?.planlist);
				const currentParsed = tryParsePlanlist(planlistValue);
				const hasPlanlistChanged =
					originalParsed && currentParsed
						? JSON.stringify(originalParsed) !== JSON.stringify(currentParsed)
						: String(planlistValue ?? "") !== String(row?.planlist ?? "");

				const planlistToSend = hasPlanlistChanged
					? currentParsed
						? JSON.stringify(currentParsed)
						: planlistValue
					: normalizePlanlistForRevision(row?.planlist);

				const updateData = {
					id: row.id,
					status: dadosValidados.status,
					obs: dadosValidados.obs + " - " + new Date().toLocaleString(),
					planlist: planlistToSend,
				};
				analysisService
					.reverteAnalysisSuperAdmin(updateData)
					.then(() => {
						handleUpdateLink(row, true);
						onClose({ shouldRefresh: true });
					})
					.catch((error) => {
						console.error("Erro ao reverter a análise:", error);
					});
			})
			.catch((errors: yup.ValidationError) => {
				const validationErros: IVFormErros = {};
				errors.inner.forEach((error) => {
					if (!error.path) return;
					validationErros[error.path] = error.message;
				});
				formRef.current?.setErrors(validationErros);
			});
	};

	const handleUpdateLink = (dados: any, isRevert: boolean) => {
		const updateLink = {
			phone: row.phone,
			email: row.email,
		};
		analysisService
			.updateSendBiometria(row.id, { ...updateLink }, isRevert)
			.then(() => {
				toastHandler.present({
					type: "success",
					position: "top-right",
					messageOrContent: <ToastContent title="Sucesso !" content="Reversão realizada com sucesso !" />,
				});
			})
			.catch((error) => {
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title="Erro !" content="Erro ao processar reversão  !" />,
				});
			});
	};

	return (
		<ModalContext
			isCloseModal={handleRequestClose}
			isOpenModal={isOpen}
			messageType={"question"}
			containerOverflow="hidden"
			overrideHeightValue="80vh"
			maxHeight="80vh"
			stylePropert={{
				display: "flex",
				flexDirection: "column",
				flex: 1,
				overflow: "hidden",
				padding: 0,
			}}
			onclickConfirm={() => {}}
			IMessage={[
				<Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
					<Box
						sx={{
							position: "sticky",
							top: 0,
							zIndex: 2,
							backgroundColor: "#fff",
							pt: 1,
							pb: 1,
							px: 1,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							borderBottom: "1px solid #eee",
						}}
					>
						<Typography variant="h5">Tem certeza que deseja reverter a análise?</Typography>
						<IconButton onClick={handleRequestClose} size="small" aria-label="Fechar" sx={{ position: "absolute", right: 8 }}>
							<CloseIcon />
						</IconButton>
					</Box>

					<VForm
						id="formRevert"
						placeholder="Your placeholder"
						ref={formRef}
						onSubmit={handleSubmit}
						onPointerEnterCapture={""}
						onPointerLeaveCapture={() => {}}
						style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
					>
						<Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", px: 1, pt: 2 }}>
							<Alert severity="warning" sx={{ textAlign: "left", mb: 2 }}>
								<Typography variant="body2">
									Ao reverter, a análise irá para etapa de biometria/aprovação e os planos/condições ativas podem ser recalculados/atualizados.
								</Typography>
								<Typography variant="body2">
									Caso esta análise seja integrada, os dados serão reenviados ao parceiro de origem para atualização.
								</Typography>
								<Typography variant="body2">Depois de confirmar, não será possível desfazer esta ação.</Typography>
							</Alert>

							<Grid container item direction="column" padding={1} spacing={2}>
								<Grid container item direction="row" spacing={2}>
									<Grid item xs={8}>
										<VTextField fullWidth label="Responsável pela autorização" name="obs" required />
									</Grid>
									<Grid item xs={4}>
										<VTextField fullWidth label="Status" name="status" required select>
											<MenuItem value="awaiting_biometria">Biometria</MenuItem>
											<MenuItem value="issued">Aprovada</MenuItem>
										</VTextField>
									</Grid>
								</Grid>

								<Grid container item direction="column" padding={1} spacing={2}>
									<Grid item xs={12}>
										<Box
											sx={{
												boxSizing: "border-box",
												textAlign: "left",
												borderRadius: 3,
												px: 2,
												py: 1.25,
												backgroundColor: "rgba(121, 36, 199, 0.04)",
												boxShadow: "rgba(17, 17, 26, 0.05) 0px 1px 0px, rgba(17, 17, 26, 0.08) 0px 0px 8px",
												display: "flex",
												flexDirection: "column",
												gap: 0.25,
											}}
										>
											<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
												<Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main" }}>
													Planos
												</Typography>
												<Button
													color="primary"
													disableElevation
													variant={"outlined"}
													startIcon={<Icon>add</Icon>}
													onClick={handleIncludeRevisionPlan}
													disabled={hasRevisionPlanInList}
													sx={{ alignSelf: "flex-start" }}
												>
													Incluir plano revision
												</Button>
											</Box>
											<Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
												Somente o status (Ativo/Inativo) pode ser alterado.
											</Typography>
										</Box>
									</Grid>
									<Grid item xs={12}>
										<Box>
											<PlanListEditor
												value={planlistValue}
												onChange={setPlanlistValue}
												planRevisionStatus={planRevisionStatus}
												revisionPlanId={revisionPlanId}
												editMode="statusOnly"
											/>
										</Box>
									</Grid>
								</Grid>
							</Grid>
						</Box>

						<Box
							sx={{
								borderTop: "1px solid #eee",
								backgroundColor: "#fff",
								px: 1.5,
								py: 1.25,
								display: "flex",
								justifyContent: "center",
								gap: 2,
							}}
						>
							<Button color="primary" disableElevation variant={"contained"} startIcon={<Icon>check</Icon>} type="submit">
								{"Reverter"}
							</Button>
							<Button
								color="primary"
								disableElevation
								variant={"outlined"}
								startIcon={<Icon>arrow_back</Icon>}
								onClick={handleRequestClose}
							>
								{"Desistir"}
							</Button>
						</Box>
					</VForm>
				</Box>,
			]}
		/>
	);
};
